import { useState } from "react";
import { useFeeOptions, useStandardDefault } from "@/components/AdvertisingTermsPicker";
import { toast } from "sonner";
import { UserPlus, Loader2 } from "lucide-react";
import { convertProspect } from "@/lib/prospectApi";
import { INVITE_STAGE } from "@/lib/inviteApi";
import { errMsg } from "@/lib/applyApi";
import { copyText } from "@/lib/clipboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const FEES = (o) => [o.standard, o.reduced, "custom"];

// Converts using the existing pre-approved seller invitation; the seller then follows the normal agreement + Ad Template flow.
export function ConvertSection({ prospect, onConverted }) {
  const [fee, setFee] = useState("10");
  const opts = useFeeOptions();
  const touch = useStandardDefault(fee, setFee, opts);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const inv = prospect.invitation;
  const convert = async () => {
    setBusy(true);
    try { onConverted(await convertProspect(prospect.id, Number(fee === "custom" ? custom : fee))); toast.success("Seller invitation created"); }
    catch (e) { toast.error(errMsg(e, "Could not convert prospect")); }
    finally { setBusy(false); }
  };
  if (inv) {
    return (
      <div data-testid="prospect-converted" className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 space-y-2">
        <p className="text-sm font-semibold text-stone-900">Converted to pre-approved seller · <span className="font-normal">{(INVITE_STAGE[inv.stage] || [inv.stage])[0]}</span></p>
        {inv.url ? <>
          <div data-testid="prospect-invite-url" className="rounded-lg border border-stone-200 bg-white p-2.5 text-xs break-all font-mono">{inv.url}</div>
          <Button data-testid="prospect-copy-invite" onClick={() => copyText(inv.url)} className="w-full h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">Copy Invitation Link</Button>
        </> : <p className="text-xs text-stone-600">Manage this seller from Applications → Pre-Approved Invitations.</p>}
      </div>
    );
  }
  return (
    <div data-testid="prospect-convert-section" className="rounded-xl border border-stone-200 p-3 space-y-2">
      <p className="text-sm font-semibold text-stone-900">Convert to Horse Sale</p>
      <p className="text-xs text-stone-500">Creates a pre-approved seller invitation with this contact and horse name. Notes and history stay with the prospect.</p>
      <div role="radiogroup" className="flex flex-wrap gap-2">
        {FEES(opts).map((f, i) => (
          <button key={i} type="button" role="radio" aria-checked={fee === f} data-testid={`prospect-fee-${["standard", "reduced", "custom"][i]}`} onClick={() => { touch(); setFee(f); }}
            className={`h-11 px-4 rounded-full border text-sm font-semibold ${fee === f ? "bg-stone-700 border-stone-700 text-white" : "bg-white border-stone-300 text-stone-700"}`}>
            {f === "custom" ? "Custom" : `${f}% fee`}
          </button>
        ))}
        {fee === "custom" && <Input data-testid="prospect-fee-custom-input" inputMode="decimal" placeholder="e.g. 7.5" value={custom} onChange={(e) => setCustom(e.target.value)} className="h-11 w-28 text-base sm:text-sm" />}
      </div>
      <Button data-testid="prospect-convert-button" onClick={convert} disabled={busy || (fee === "custom" && !custom)} variant="outline" className="w-full h-11 rounded-full border-amber-800 text-amber-800 hover:bg-amber-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1.5" />Create Seller Invitation</>}
      </Button>
    </div>
  );
}
